/**
 * This file provides a global reference to the Redux store via `setStore` / `getStore`.
 *
 * Why not just import { store } directly?
 * - Direct imports "lock in" the default store at module load time.
 * - If functions import store directly are used in Redux actions or thunks, it can cause undefined modules
 *
 * The pattern here:
 * - `setStore(store)` is called once during app initialization (in app.tsx, right before
 *   rendering the <Provider>).
 * - In tests, `setStore` can be called to swap in a new store.
 * - Any non-React code that needs store access (e.g. helpers, API interceptors, salesforce one chat helpers) calls
 *   `getStore()` instead of importing the store directly.
 *
 * React components and Redux functions should not change the way they access the store
 *
 * TL;DR: This breaks the direct import cycle and makes the store swappable for tests.
 */

import type { AppStore } from './store';

let _store: AppStore;

/**
 * ONLY CALL THIS ONCE DURING APP INITIALIZATION!
 *
 * or in tests
 */
export const setStore = (s: AppStore) => {
  _store = s;
};

export const getStore = () => {
  if (!_store) throw new Error('Store has not been set yet!');
  return _store;
};
